// @flow

import MainConnector from './MainConnector'
import Utils from './Utils'
import ErrorManager from './ErrorManager'
import DMPCodes from './DMPCodes'

/**
 *
 * TD1.X transactions management
 * @class CreateConnector
 * @extends {MainConnector}
 */
class CreateConnector extends MainConnector {
    PatientData: any

    VitaleData: any

    PatientOppositions: any

    i_guardian: any

    GuardianData: any

    i_createOTP: any

    /**
     * Default constructor
     * @param {boolean} verbose = if true verbose
     * @param {boolean} testEnv = if true test env (eg. not real life DMP)
     */
    constructor(verbose: boolean = false, testEnv: boolean = false) {
        super(verbose, testEnv)
        this.PatientData = {}
    }

    /**
     * Add Patients's Ins
     * @param {string} insWithType Ins with type eg. XXXXX + last caracter is insType exampe -> 198019999999931D
     */
    initPatientDataWithIns(insWithType: string) {
        if (!Utils.checkNirOrInsIsValid(insWithType)) {
            ErrorManager.sendError('invalid ins format should be string of 15 or 16 length (source - initPatientData)')
        }

        this.PatientData.s_ins = insWithType
    }

    /**
     * Add civil and vital infos for patient (the infos here must match data on vital card)
     ** in the future we could make two separate functions if we want diff on vital data and civil data
     ** currently it is the same
     * @param {string} gender, F = female, M = Male
     * @param {string} lastName
     * @param {string} firstName
     * @param {string} birthName
     * @param {string} birthDate = date of birth for patient with format : YYYYMMDD
     * @param {string} birthCountry
     */
    initPatientCivilStatusAndVitalData(
        gender: string,
        lastName: string,
        firstName: string,
        birthName: string,
        birthDate: string,
        birthCountry: string,
    ) {
        if (gender !== 'F' && gender !== 'M') {
            ErrorManager.sendWarning(
                'unknown gender format should be string, value of F or M (source - initPatientCivilStatus)',
            )
        }

        if (!Utils.checkBirthDate(birthDate)) {
            ErrorManager.sendError(
                'invalid format for birthDate, format should be YYYYMMDD (source - initPatientCivilStatus)',
            )
        }

        let genderCode = DMPCodes.Gender.UnknownGender
        let sexCode = DMPCodes.Sex.UnknownSex
        if (gender === 'M') {
            genderCode = DMPCodes.Gender.M
            sexCode = DMPCodes.Sex.Male
        }
        if (gender === 'F') {
            genderCode = DMPCodes.Gender.Mme
            sexCode = DMPCodes.Sex.Female
        }

        this.PatientData.ExtendedCivilStatus = {
            CivilStatus: {
                i_gender: genderCode,
                s_name: lastName,
                s_given: firstName,
            },
            s_birthName: birthName,
            s_birthDay: birthDate, // YYYYMMDD
            s_birthCountry: birthCountry,
            i_sex: sexCode,
        }

        this.VitaleData = {
            s_name: lastName,
            s_birthName: birthName,
            s_given: firstName,
            s_birthDay: Utils.formatBirthDateFromEightToSix(birthDate), // YYMMDD
        }
    }

    /**
     * Add contact details infos
     * @param {string} mobilePhone = mobile phone with no check on format
     * @param string landlinePhone = home phone with no check on format
     * @param string mail  = email address with no check on format
     * @param string address = address
     * @param string adressComplement = address complement if necessary
     * @param string postalCode = postalCode
     * @param string city  = city name
     * @param string country  = country name
     */
    initPatientContactDetails(
        mobilePhone: string,
        landlinePhone: string,
        mail: string,
        address: string,
        adressComplement: string,
        postalCode: string,
        city: string,
        country: string,
    ) {
        this.PatientData.ExtendedContactDetails = {
            ContactDetails: {
                s_mobilePhone: mobilePhone,
                s_landlinePhone: landlinePhone,
                s_mail: mail,
                s_address: address,
                s_addressComplement: adressComplement,
                s_postalCode: postalCode,
                s_city: city,
            },
            s_country: country,
        }
    }

    /**
     * Add Oppositions data
     * @param {boolean} accesptsBrisdeGlace, if true then patient accepts HP to access his DMP on "bris de glace"
     * @param {boolean} acceptsEmegencyAccess , if true patient accepts emergency profesionnals to access his DMP
     */
    initPatientOppositions(accesptsBrisdeGlace: boolean, acceptsEmegencyAccess: boolean) {
        this.PatientOppositions = {
            i_brisDeGlaceOpposition: accesptsBrisdeGlace ? 1 : 0,
            i_centre15Opposition: acceptsEmegencyAccess ? 1 : 0,
        }
    }

    /**
     * Add no guardian data to connector
     */
    initNoGuardian() {
        this.i_guardian = DMPCodes.GuardianStatus.NoGuardianDefined
    }

    /**
     * Add a guardian for patient
     * @param {numbner} gRole, the role with values Father :  1, Mother :  2, StepFather: 3, StepMother :  4, GrandFather :  5, GrandMother :  6, GrandGrandFather :  7, GrandGrandMother :  8, Aunt:  9, Uncle : 10, Brother : 11, Sister : 12, Guardian: 13
     * @param {string} gGenger, F = female, M = Male
     * @param string gLastName
     * @param string gFirstName
     * @param string gMobilePhone  = mobile phone with no check on format
     * @param string gLanlinePhone  = home with no check on format
     * @param string gMail  = email with no check on format
     * @param string gAddress  = address
     * @param string gAddressComplement  = address complements if necessary
     * @param string gPostalCode  = postalCode with no check on format
     * @param string gCity  = city with no check on format
     */
    initGuardian(
        gRole: number,
        gGenger: string,
        gLastName: string,
        gFirstName: string,
        gMobilePhone: string,
        gLanlinePhone: string,
        gMail: string,
        gAddress: string,
        gAddressComplement: string,
        gPostalCode: string,
        gCity: string,
    ) {
        let genderCode = DMPCodes.Gender.UnknownGender
        if (gGenger === 'M') {
            genderCode = DMPCodes.Gender.M
        }
        if (gGenger === 'F') {
            genderCode = DMPCodes.Gender.Mme
        }

        this.i_guardian = DMPCodes.GuardianStatus.GuardianDefined
        this.GuardianData = {
            i_role: gRole,
            CivilStatus: {
                i_gender: genderCode,
                s_name: gLastName,
                s_given: gFirstName,
            },
            ContactDetails: {
                s_mobilePhone: gMobilePhone,
                s_landlinePhone: gLanlinePhone,
                s_mail: gMail,
                s_address: gAddress,
                s_addressComplement: gAddressComplement,
                s_postalCode: gPostalCode,
                s_city: gCity,
            },
        }
    }

    /**
     * Adds param to generate pdf file for DMP access to give to patient
     * @param {boolean}, if true will generate
     */
    initActivatePatientWebAccessSecrets(generate: boolean) {
        this.i_createOTP = generate ? 1 : 0
    }

    /**
     * Transaction TD1.1 Launches request to create a new DMP for Patient
     * @returns {Promise<Object>} the answer in format { "s_pdfFileContentInBase64" : "", "s_status" : "OK" }
     ** s_pdfFileContentInBase64 is the PDF in base64 for patient access infos only returned if i_createOTP is set to 1 in base request
     */
    async launchDmpCreation(): Promise<any> {
        const requestEndPoint = 'createdmp'

        if (!this.PatientData) {
            ErrorManager.sendError(
                'invalid request missing PatientData object, call initPatientDataWithIns first (source - PatientData)',
            )
        }

        if (!this.VitaleData) {
            ErrorManager.sendError(
                'invalid request missing VitaleDatas object, call initPatientCivilStatusAndVitalData first (source - PatientData)',
            )
        }

        if (!this.PatientData.ExtendedCivilStatus) {
            ErrorManager.sendError(
                'invalid request missing PatientData.ExtendedCivilStatus object, call initPatientCivilStatusAndVitalData first (source - PatientData)',
            )
        }

        if (!this.PatientData.ExtendedContactDetails) {
            ErrorManager.sendError(
                'invalid request missing PatientData.ExtendedContactDetails object, call initPatientContactDetails first (source - PatientData)',
            )
        }

        if (!this.PatientOppositions) {
            ErrorManager.sendError(
                'invalid request missing PatientOppositions object, call initPatientOppositions first (source - PatientData)',
            )
        }

        if (!this.i_guardian) {
            ErrorManager.sendError(
                'invalid request missing i_guardian object, call initNoGuardian or initGuardian first (source - PatientData)',
            )
        }

        if (this.i_guardian && !this.GuardianData) {
            ErrorManager.sendError(
                'invalid request missing GuardianData object, call initGuardian first (source - PatientData)',
            )
        }

        if (!this.i_createOTP) {
            ErrorManager.sendError(
                'invalid request missing i_createOTP object, call initActivatePatientWebAccessSecrets first (source - PatientData)',
            )
        }

        const extraEndPointParams = {
            DmpAdministrativeData: {
                PatientData: {
                    ...this.PatientData,
                },
                PatientOppositions: {
                    ...this.PatientOppositions,
                },
                i_guardian: this.i_guardian,
                GuardianData: undefined,
            },
            i_createOTP: this.i_createOTP,
            VitaleData: {
                ...this.VitaleData,
            },
        }

        if (this.GuardianData) {
            extraEndPointParams.DmpAdministrativeData.GuardianData = {
                ...this.GuardianData,
            }
        }

        const result = await this.makeRequest(requestEndPoint, extraEndPointParams)
        return result
    }

    /**
     * Transaction TD1.2 Re-activates a disabled dmp
     * @param {string} insWithType
     * @param string gender
     * @param string lastName
     * @param string firstName
     * @param string birthDate format YYYYDDMM
     * @param string birthName
     */
    async reActivateDmp(
        insWithType: string,
        gender: string,
        lastName: string,
        firstName: string,
        birthDate: string,
        birthName: string,
    ) {
        const requestEndPoint = 'reactivatedmp'

        if (gender !== 'F' && gender !== 'M') {
            ErrorManager.sendWarning('unknown gender format should be string, value of F or M (source - reActivateDmp)')
        }

        if (!Utils.checkBirthDate(birthDate)) {
            ErrorManager.sendError('invalid format for birthDate, format should be YYYYMMDD (source - reActivateDmp)')
        }

        let genderCode = DMPCodes.Gender.UnknownGender
        let sexCode = DMPCodes.Sex.UnknownSex
        if (gender === 'M') {
            genderCode = DMPCodes.Gender.M
            sexCode = DMPCodes.Sex.Male
        }
        if (gender === 'F') {
            genderCode = DMPCodes.Gender.Mme
            sexCode = DMPCodes.Sex.Female
        }

        const DmpAdministrativeData: any = undefined
        const extraEndPointParams = {
            s_ins: insWithType,
            i_gender: genderCode,
            s_given: firstName,
            s_name: lastName,
            i_sex: sexCode,
            s_birthDay: birthDate,
            s_birthName: birthName,
            DmpAdministrativeData,
        }

        if (this.GuardianData) {
            extraEndPointParams.DmpAdministrativeData = {
                GuardianData: {
                    ...this.GuardianData,
                },
            }
        }

        const result = await this.makeRequest(requestEndPoint, extraEndPointParams)
        return result
    }
}

export default CreateConnector
